import React from 'react';
import './StupidSystemDesigns.css';

const spellCheckerDesignCode = String.raw`tokenize(paragraph):
    words = split paragraph using whitespace
    result = []

    for each word in words:
        word = normalize(word)

        if word is not empty:
            result.add(word)

    return result

getSuggestions(paragraph):
    words = tokenizer.tokenize(paragraph)
    result = {}

    for each word in words:
        if dictionary.isPresent(word):
            continue

        if word not in result:
            result[word] = worker.getSuggestionsForWord(word)

    return result

getSuggestionsForWord(word):
    candidates = []

    for each candidate in dictionary:
        if abs(length(word) - length(candidate)) > MAX_DISTANCE:
            continue

        distance = editDistance(word, candidate)

        if distance <= MAX_DISTANCE:
            candidates.add(candidate, distance)

    sort candidates by distance ascending
    return top K candidates

editDistance(a, b):
    dp = 2D array of size (length(a)+1) x (length(b)+1)

    for i = 0 to length(a):
        dp[i][0] = i

    for j = 0 to length(b):
        dp[0][j] = j

    for i = 1 to length(a):
        for j = 1 to length(b):
            if a[i-1] == b[j-1]:
                cost = 0
            else:
                cost = 1

            dp[i][j] = min(
                dp[i-1][j] + 1,       // deletion
                dp[i][j-1] + 1,       // insertion
                dp[i-1][j-1] + cost   // substitution
            )

    return dp[length(a)][length(b)]`;

function SpellCheckerDesign({ theme, onBackClick }) {
    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Spell Checker Low Level Design</h1>
                            <p className="system-designs-subtitle">
                                Find misspellings and suggest likely corrections with a replaceable strategy.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Requirements</h2>
                            <ul>
                                <li>Accept a paragraph as input.</li>
                                <li>Maintain an internal dictionary of correctly spelled words.</li>
                                <li>Identify misspelled words.</li>
                                <li>Suggest the top probable corrections for each misspelled word.</li>
                                <li>Support adding new words to the dictionary.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Core Entities</h2>
                            <ul>
                                <li>
                                    <strong>SpellCheckerController:</strong> Coordinates tokenization, dictionary lookups, and suggestions.
                                    It owns a tokenizer, dictionary, and worker, and exposes <code>getSuggestions(paragraph)</code>.
                                </li>
                                <li><strong>Tokenizer:</strong> Converts a paragraph into individual words using <code>tokenize(paragraph)</code>.</li>
                                <li>
                                    <strong>Dictionary:</strong> Stores correctly spelled words in <code>words: Set&lt;String&gt;</code>.
                                    It supports <code>addWord(word)</code> and <code>isPresent(word)</code>; a hash set provides average O(1) lookup.
                                </li>
                                <li>
                                    <strong>SpellCheckerWorker:</strong> Uses the dictionary to identify misspellings and generate corrections through
                                    {' '}<code>spellCheck(words)</code> and <code>getSuggestionsForWord(word)</code>.
                                </li>
                                <li>
                                    <strong>SuggestionStrategy:</strong> Makes the correction algorithm replaceable without changing the worker.
                                    <code>EditDistanceStrategy</code> is one implementation; <code>TrieBasedStrategy</code> is a future enhancement.
                                </li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Pseudocode</h2>
                            <pre className="design-code-block"><code>{spellCheckerDesignCode}</code></pre>
                        </div>

                        <div className="design-section">
                            <h2>Edit Distance and Complexity</h2>
                            <p className="system-design-description">
                                Levenshtein distance measures the minimum number of insertions, deletions, and substitutions needed to transform one word into another.
                                The dynamic-programming implementation takes O(m × n) time per word comparison, where m and n are the word lengths.
                            </p>
                            <p className="system-design-description">
                                Comparing every input word against a large dictionary can become expensive. The length-difference check avoids comparisons that
                                cannot meet the maximum distance, and future improvements can use a trie or bounded edit distance to narrow the search further.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Future Improvements</h2>
                            <ol>
                                <li><strong>Trie:</strong> Organize dictionary words by prefixes to help narrow down candidate words.</li>
                                <li><strong>Optimized edit distance:</strong> Filter candidates by length and use bounded distance to avoid unnecessary calculations.</li>
                                <li><strong>Ranking:</strong> Rank suggestions using edit distance, word frequency, and contextual relevance.</li>
                                <li><strong>Caching:</strong> Cache suggestions for frequently encountered misspellings.</li>
                                <li><strong>Alternative strategies:</strong> Implement different suggestion algorithms behind <code>SuggestionStrategy</code>.</li>
                            </ol>
                            <p className="system-design-description">
                                <strong>Core design principle:</strong> The controller coordinates the workflow, the tokenizer extracts words, the dictionary validates spelling,
                                and the worker generates corrections using a replaceable suggestion strategy.
                            </p>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        shoo, run away before i put a spell on you. Then you can use spell checker to see if you have a spell or not.
                    </div>

                    <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default SpellCheckerDesign;
